import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Calendar, Tag, ChevronRight } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_POSTS = [
  {
    id: '1',
    title: { it: 'Nuove vie a Cabeci', en: 'New routes at Cabeci', fr: 'Nouvelles voies à Cabeci', de: 'Neue Routen in Cabeci', es: 'Nuevas vías en Cabeci' },
    excerpt: { it: 'Abbiamo completato l\'apertura di 5 nuove vie nel settore di Cabeci. Scopri i dettagli!', en: 'We completed the opening of 5 new routes in the Cabeci sector. Discover the details!', fr: 'Nous avons terminé l\'ouverture de 5 nouvelles voies dans le secteur de Cabeci.', de: 'Wir haben die Erschließung von 5 neuen Routen im Sektor Cabeci abgeschlossen.', es: 'Hemos completado la apertura de 5 nuevas vías en el sector de Cabeci.' },
    content: { it: 'Contenuto completo dell\'articolo...', en: 'Full article content...', fr: 'Contenu complet de l\'article...', de: 'Vollständiger Artikelinhalt...', es: 'Contenido completo del artículo...' },
    category: 'news',
    tags: ['cabeci', 'nuove vie'],
    featured_image: 'https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/exhuhqnq_Falesia%20di%20Cabeci.jpg',
    published_at: '2024-11-15',
    slug: 'nuove-vie-cabeci'
  },
  {
    id: '2',
    title: { it: 'Guida ai gradi di Caltavuturo', en: 'Caltavuturo grades guide', fr: 'Guide des cotations de Caltavuturo', de: 'Schwierigkeitsgrade in Caltavuturo', es: 'Guía de grados de Caltavuturo' },
    excerpt: { it: 'Una guida completa per capire i gradi delle vie nelle nostre falesie.', en: 'A complete guide to understanding route grades at our crags.', fr: 'Un guide complet pour comprendre les cotations de nos falaises.', de: 'Ein vollständiger Leitfaden zum Verständnis der Schwierigkeitsgrade.', es: 'Una guía completa para entender los grados de nuestros sectores.' },
    content: { it: 'Contenuto completo...', en: 'Full content...', fr: 'Contenu complet...', de: 'Vollständiger Inhalt...', es: 'Contenido completo...' },
    category: 'guide',
    tags: ['gradi', 'guida'],
    featured_image: 'https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/2fwl0gnc_falesia%20gazzara.jpeg',
    published_at: '2024-10-20',
    slug: 'guida-gradi'
  },
  {
    id: '3',
    title: { it: 'Stagione invernale 2024', en: 'Winter season 2024', fr: 'Saison d\'hiver 2024', de: 'Wintersaison 2024', es: 'Temporada de invierno 2024' },
    excerpt: { it: 'Consigli per arrampicare nelle nostre falesie durante l\'inverno siciliano.', en: 'Tips for climbing at our crags during the Sicilian winter.', fr: 'Conseils pour grimper sur nos falaises pendant l\'hiver sicilien.', de: 'Tipps zum Klettern während des sizilianischen Winters.', es: 'Consejos para escalar durante el invierno siciliano.' },
    content: { it: 'Contenuto completo...', en: 'Full content...', fr: 'Contenu complet...', de: 'Vollständiger Inhalt...', es: 'Contenido completo...' },
    category: 'tips',
    tags: ['inverno', 'consigli'],
    featured_image: 'https://customer-assets.emergentagent.com/job_sport-climb-cms/artifacts/k9h46qr8_Falesia%20Rocca%20di%20Sciara.jpg',
    published_at: '2024-12-01',
    slug: 'stagione-invernale'
  }
];

export function BlogPage() {
  const { t, getTranslatedContent, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get(`${API}/blog`);
        setPosts(res.data || []);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const categories = ['all', 'news', 'guide', 'tips', 'events'];
  const filteredPosts = selectedCategory === 'all' 
    ? posts 
    : posts.filter(p => p.category === selectedCategory);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <section className="relative py-32 bg-[#0A0A0A]">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <BookOpen className="w-16 h-16 text-[#FF6D00] mx-auto mb-6" />
            <h1 className="heading-xl text-white mb-6">
              {t('blog')}
            </h1>
            <p className="body-lg text-gray-400 max-w-2xl mx-auto">
              {language === 'it'
                ? 'Notizie, guide e storie dal mondo dell\'arrampicata a Caltavuturo.'
                : 'News, guides and stories from the climbing world in Caltavuturo.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 border-b">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-[#FF6D00] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat === 'all' ? (language === 'it' ? 'Tutti' : 'All') : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500">{t('noResults')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post, i) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white border rounded-lg overflow-hidden group hover:border-[#FF6D00]/50 transition-colors"
                >
                  {/* Image */}
                  <Link to={`/team/blog/${post.slug || post.id}`}>
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.featured_image || 'https://images.unsplash.com/photo-1522163182402-834f871fd851?w=600'}
                        alt={getTranslatedContent(post.title)}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                  </Link>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="bg-[#FF6D00]/10 text-[#FF6D00] text-xs px-2 py-1 rounded uppercase">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-gray-500 text-xs">
                        <Calendar className="w-3 h-3" />
                        {formatDate(post.published_at)}
                      </span>
                    </div>

                    <Link to={`/team/blog/${post.slug || post.id}`}>
                      <h3 className="font-heading text-xl uppercase mb-3 hover:text-[#FF6D00] transition-colors">
                        {getTranslatedContent(post.title)}
                      </h3>
                    </Link>
                    
                    <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                      {getTranslatedContent(post.excerpt)}
                    </p>

                    {/* Tags */}
                    {post.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, j) => (
                          <span key={j} className="inline-flex items-center gap-1 text-gray-500 text-xs">
                            <Tag className="w-3 h-3" />
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <Link 
                      to={`/team/blog/${post.slug || post.id}`}
                      className="inline-flex items-center text-[#FF6D00] font-semibold text-sm hover:underline"
                    >
                      {t('readMore')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
